import { someTrue } from 'aa_common/front-end/helpers'
import { DEPRECIATION_METHOD_CODE } from 'common/constants'
import { isAccountingJournalTarget } from 'common/helpers'
import { getNs } from 'common/i18n-config'
import { AssetLedger } from 'common/models'
import { useActionsStatus } from 'containers/assets/asset-list-container/hooks/use-actions-status'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectAssetDetailPage } from 'store/asset/selectors'

import { useAcceleratedDeprPermission } from './use-accelerated-depr-permission'
import { useIsEventAfterAccelerated } from './use-event-after-accelerated'

const { t } = getNs(['common', 'components/organisms/assets/asset-detail'])

export const useAcceleratedDepreciationTooltip = (assetLedger: AssetLedger) => {
  const { asset } = useSelector(selectAssetDetailPage, isEqual)
  const { findActionsStatusByBiid } = useActionsStatus()
  const actionsStatusByBiid = findActionsStatusByBiid(asset?.biid as string)
  const isEventAfterAccelerated = useIsEventAfterAccelerated(assetLedger)
  const isFullyDepreciatedFixedAsset = someTrue(Boolean(actionsStatusByBiid?.depr_finished), isEventAfterAccelerated)

  const accountantLedger = asset?.fixed_asset_ledgers?.find(ledger => isAccountingJournalTarget(ledger.ledger_setting))
  const { isCRU: havePermission } = useAcceleratedDeprPermission()

  return useMemo(() => {
    const depreciationMethodCode = get(assetLedger, 'depreciation_method_code') as number
    const isMethodExcluded = [
      DEPRECIATION_METHOD_CODE.SMALL_AMOUNT_DEPRECIABLE_ASSET,
      DEPRECIATION_METHOD_CODE.NON_DEPRECIABLE,
      DEPRECIATION_METHOD_CODE.NON_DEPRECIATION,
    ].includes(depreciationMethodCode)
    const isNotDepreciableMethod = [
      DEPRECIATION_METHOD_CODE.NON_DEPRECIABLE,
      DEPRECIATION_METHOD_CODE.NON_DEPRECIATION,
    ].includes(depreciationMethodCode)

    const isSoldOrRetired = someTrue(!!asset?.sold_at, !!asset?.retired_at)
    const isImpaired = !!get(accountantLedger, 'accumulated_impaired_amount')

    const CONTINUE_DEPRECIATION_CODE = 1
    const isContinueToDepreciate =
      depreciationMethodCode === DEPRECIATION_METHOD_CODE.BULK_EQUAL_INSTALLMENT_METHOD &&
      assetLedger?.ledger_setting?.bulk_equal_installment_depreciation_method === CONTINUE_DEPRECIATION_CODE

    const isDisabled = someTrue(
      !havePermission,
      isMethodExcluded,
      isFullyDepreciatedFixedAsset,
      isSoldOrRetired,
      isContinueToDepreciate,
      isImpaired,
      !actionsStatusByBiid?.adjustable,
      Boolean(actionsStatusByBiid?.depr_finished)
    )

    const tooltipContentByDMethod: { [key: number]: string } = {
      [DEPRECIATION_METHOD_CODE.BULK_EQUAL_INSTALLMENT_METHOD]: t('tooltip_content.cannot_adjust_with_d30'),
      [DEPRECIATION_METHOD_CODE.SMALL_AMOUNT_DEPRECIABLE_ASSET]: t('tooltip_content.cannot_adjust_with_d80'),
      [DEPRECIATION_METHOD_CODE.NON_DEPRECIABLE]: t('tooltip_content.cannot_adjust_with_d90'),
      [DEPRECIATION_METHOD_CODE.NON_DEPRECIATION]: t('tooltip_content.cannot_adjust_with_d99'),
    }

    const getTooltipContent = () => {
      if (!havePermission) {
        return t('actions.no_permission')
      }
      if (isNotDepreciableMethod) {
        return tooltipContentByDMethod[depreciationMethodCode]
      }
      if (isFullyDepreciatedFixedAsset) {
        return t('tooltip_content.fully_depreciated_fixed_asset')
      }

      if (someTrue(isSoldOrRetired, isImpaired)) {
        return t('tooltip_content.cannot_adjust_with_events')
      }

      return tooltipContentByDMethod[depreciationMethodCode]
    }

    return { isDisabled, tooltipContent: getTooltipContent() }
  }, [
    asset,
    assetLedger,
    accountantLedger,
    havePermission,
    isFullyDepreciatedFixedAsset,
    JSON.stringify(actionsStatusByBiid),
  ])
}
